/* eslint-disable react-hooks/refs -- pre-existing pattern flagged by the React
   Compiler rules added in eslint-plugin-react-hooks v7; refactoring it is
   out of scope for the lint toolchain upgrade. */
import { useCallback, useEffect, useRef, useState } from 'react'

import type {
  FetchSourceHandler,
  LockedAttachmentSource,
  PaymentStatus,
} from '../../types'

export interface UseReceivedSourceOptions {
  paymentStatus?: PaymentStatus
  onFetchSource?: FetchSourceHandler
  /** Called on Unlock click while the card is still unpaid (opens checkout). */
  onUnlockClick?: () => void
}

export interface UseReceivedSourceResult {
  source: LockedAttachmentSource | undefined
  /** Attach to the card root so the paid-source auto-fetch can observe it. */
  cardRef: React.RefObject<HTMLDivElement>
  /** Fetches the source when already paid, else defers to `onUnlockClick`. */
  handleUnlockClick: () => void
}

/**
 * Media !isMine's source-reveal logic: unlike Composer/Media isMine's
 * tap-to-toggle (`usePreviewToggle`), the recipient's card reveals its source
 * by fetching once — on click when already paid, or automatically the first
 * time a paid card scrolls fully into view. The fetch is guarded so it runs
 * at most once in flight.
 */
export function useReceivedSource({
  paymentStatus,
  onFetchSource,
  onUnlockClick,
}: UseReceivedSourceOptions): UseReceivedSourceResult {
  const [source, setSource] = useState<LockedAttachmentSource | undefined>()

  const cardRef = useRef<HTMLDivElement>(null)
  const fetchingRef = useRef(false)

  const onFetchSourceRef = useRef(onFetchSource)
  onFetchSourceRef.current = onFetchSource

  const fetchSource = useCallback(async (): Promise<void> => {
    if (fetchingRef.current) return
    fetchingRef.current = true
    try {
      const result = await onFetchSourceRef.current?.()
      if (result) setSource(result)
    } finally {
      fetchingRef.current = false
    }
  }, [])

  const handleUnlockClick = useCallback(() => {
    if (paymentStatus === 'paid') {
      void fetchSource()
    } else {
      onUnlockClick?.()
    }
  }, [paymentStatus, fetchSource, onUnlockClick])

  // Auto-fetch the source once the paid card scrolls into view.
  useEffect(() => {
    if (!cardRef.current) return
    if (paymentStatus !== 'paid' || source !== undefined) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          void fetchSource()
          observer.disconnect()
        }
      },
      { threshold: 1.0 }
    )

    observer.observe(cardRef.current)
    return () => observer.disconnect()
  }, [paymentStatus, source, fetchSource])

  return { source, cardRef, handleUnlockClick }
}
